import {
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import { useEffect, useMemo, useState } from "react";
import Header from "src/components/Header";
import evaluacionAPI, {
  CrearEvaluacionPayload,
  RespuestaEvaluacion,
} from "src/services/evaluacionCapacitacion";

type Capacitador = {
  cod_empleado?: string;
  cedula: string;
  nombre_completo: string;
  cargo?: string;
};

type Pregunta = {
  id: number;
  pregunta: string;
  activa: boolean;
};

const EvaluacionCapacitacion: React.FC = () => {
  const [capacitadores, setCapacitadores] = useState<Capacitador[]>([]);
  const [preguntas, setPreguntas] = useState<Pregunta[]>([]);
  const [capacitadorSeleccionado, setCapacitadorSeleccionado] = useState<string>("");
  const [puntaje, setPuntaje] = useState<string>("");
  const [comentario, setComentario] = useState<string>("");
  const [recomendacion, setRecomendacion] = useState<string>("");
  const [respuestas, setRespuestas] = useState<Record<number, boolean | null>>({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [pendienteTrimestre, setPendienteTrimestre] = useState<boolean>(false);
  const [present] = useIonToast();

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [capacitadoresRes, preguntasRes] = await Promise.all([
          evaluacionAPI.obtenerCapacitadoresTecnicos(),
          evaluacionAPI.obtenerPreguntas(),
        ]);
        const listaCap = capacitadoresRes.data?.datos || [];
        const listaPreg = preguntasRes.data?.datos || [];
        setPendienteTrimestre(!!preguntasRes.data?.pendiente_trimestre);
        setCapacitadores(listaCap);
        setPreguntas(listaPreg);
        const inicial: Record<number, boolean | null> = {};
        listaPreg.forEach((p: Pregunta) => {
          inicial[p.id] = null;
        });
        setRespuestas(inicial);
      } catch (error: any) {
        present({
          message:
            error?.response?.data?.error ||
            "No se pudieron cargar los datos iniciales",
          duration: 4000,
          color: "danger",
        });
      } finally {
        setCargando(false);
      }
    };
    cargarDatos();
  }, [present]);

  const preguntasPendientes = useMemo(() => {
    return Object.values(respuestas).some((r) => r === null);
  }, [respuestas]);

  const handleRespuesta = (preguntaId: number, valor: boolean) => {
    setRespuestas((estado) => ({
      ...estado,
      [preguntaId]: valor,
    }));
  };

  // Calcular puntaje automáticamente (escala 0-10) según proporción de respuestas "Sí"
  useEffect(() => {
    if (!preguntas.length) {
      setPuntaje("");
      return;
    }
    const total = preguntas.length;
    const respondedAll = preguntas.every((p) => respuestas[p.id] !== null && respuestas[p.id] !== undefined);
    if (!respondedAll) {
      setPuntaje("");
      return;
    }
    const siCount = preguntas.reduce((acc, p) => acc + (respuestas[p.id] === true ? 1 : 0), 0);
    const score = Math.round((siCount / total) * 10);
    setPuntaje(String(score));
  }, [preguntas, respuestas]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!capacitadorSeleccionado) {
      present({ message: "Seleccione un capacitador técnico", duration: 3000, color: "warning" });
      return;
    }
    if (preguntasPendientes) {
      present({ message: "Complete todas las respuestas", duration: 3000, color: "warning" });
      return;
    }

    const puntajeNumero = Number(puntaje || 0);

    const respuestasPayload: RespuestaEvaluacion[] = preguntas.map((p) => ({
      pregunta_id: p.id,
      respuesta: Boolean(respuestas[p.id]),
    }));

    const payload: CrearEvaluacionPayload = {
      cedula_capacitador: capacitadorSeleccionado,
      puntaje: puntajeNumero,
      comentario: comentario || undefined,
      recomendacion: recomendacion || undefined,
      respuestas: respuestasPayload,
    };

    setGuardando(true);
    try {
      const respuesta = await evaluacionAPI.crearEvaluacion(payload);
      present({
        message: respuesta.data?.info || "Evaluación guardada correctamente",
        duration: 4000,
        color: "success",
      });
      setPuntaje("");
      setComentario("");
      setRecomendacion("");
      const reinicio: Record<number, boolean | null> = {};
      preguntas.forEach((p) => (reinicio[p.id] = null));
      setRespuestas(reinicio);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo guardar la evaluación",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Evaluación a Capacitaciones Técnicas" />
      <IonContent id="evaluacion-capacitacion" className="ion-padding eval-capacitacion">
        <form onSubmit={handleSubmit}>
          <IonList>
            {pendienteTrimestre && (
              <IonItem lines="none" color="warning">
                <IonLabel color="danger" className="ion-text-wrap">
                  Recuerda realizar al menos una evaluación a capacitadores este trimestre.
                </IonLabel>
              </IonItem>
            )}
            <IonItem>
              <IonLabel position="stacked">Capacitador técnico</IonLabel>
              <IonSelect
                value={capacitadorSeleccionado}
                placeholder={cargando ? "Cargando..." : "Seleccione un capacitador"}
                disabled={cargando || guardando}
                interface="alert"
                okText="Aceptar"
                cancelText="Cancelar"
                interfaceOptions={{ cssClass: 'select-capacitador-alert' }}
                onIonChange={(e) => setCapacitadorSeleccionado(e.detail.value)}
              >
                {capacitadores.map((cap) => (
                  <IonSelectOption key={cap.cedula} value={cap.cedula} style={{ whiteSpace: "normal" }}>
                    {cap.nombre_completo} - CI {cap.cedula}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem lines="none">
              <IonNote color={preguntasPendientes ? "warning" : "medium"}>
                Responda todas las preguntas: Sí / No
              </IonNote>
            </IonItem>

            {preguntas.map((pregunta) => (
              <IonItem key={pregunta.id} lines="full">
                <div style={{ width: "100%" }}>
                  <IonLabel style={{ whiteSpace: "normal" }}>{pregunta.pregunta}</IonLabel>
                  <IonRadioGroup
                    value={respuestas[pregunta.id]}
                    onIonChange={(e) => handleRespuesta(pregunta.id, e.detail.value)}
                  >
                    <IonItem lines="none">
                      <IonLabel>Sí</IonLabel>
                      <IonRadio slot="start" value={true} disabled={guardando} />
                    </IonItem>
                    <IonItem lines="none">
                      <IonLabel>No</IonLabel>
                      <IonRadio slot="start" value={false} disabled={guardando} />
                    </IonItem>
                  </IonRadioGroup>
                </div>
              </IonItem>
            ))}

            <IonItem>
              <IonLabel position="stacked">Comentario (opcional)</IonLabel>
              <IonTextarea
                value={comentario}
                disabled={guardando}
                autoGrow
                rows={3}
                placeholder="Observaciones generales"
                onIonChange={(e) => setComentario(e.detail.value || "")}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked" className="ion-text-wrap" style={{ whiteSpace: "normal" }}>
                ¿Qué recomendaría al capacitador para futuras instancias? (opcional)
              </IonLabel>
              <IonTextarea
                value={recomendacion}
                disabled={guardando}
                autoGrow
                rows={3}
                style={{ fontSize: "14px" }}
                placeholder="Recomendaciones para próximas capacitaciones"
                onIonChange={(e) => setRecomendacion(e.detail.value || "")}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Puntaje (0 a 10)</IonLabel>
              <IonInput
                type="number"
                value={puntaje}
                min="0"
                max="10"
                readonly
                disabled
                placeholder="Se calcula automáticamente"
              />
            </IonItem>
          </IonList>

          <IonButton
            expand="block"
            type="submit"
            className="ion-margin-top"
            disabled={guardando || cargando}
          >
            {guardando ? "Guardando..." : "Enviar evaluación"}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
};

export default EvaluacionCapacitacion;
